import React, { useMemo, useState } from "react";
import { ChevronDown, ChevronUp, Delete, X } from "react-feather";
import { useQuery } from "react-query";
import {
  CSSObjectWithLabel,
  default as ReactSelect,
  StylesConfig,
  components,
} from "react-select";
import CreatableSelect from "react-select/creatable";
import styled, { css } from "styled-components";
import Button, { Props as ButtonProps } from "./Button";
import ClickOutside from "./ClickOutside";
import Label from "./Label";
import useDebounce from "./hooks/useDebounce";
import Description from "./Description";

export interface Option {
  value: string | undefined;
  label: React.ReactNode | undefined;
  extra?: string;
  group?: string;
  metadata?: any;
}

interface Group {
  label: string;
  options: Option[];
}

type Props<TMulti extends boolean = false, T = Option[]> = {
  options?: Option[];
  isSearchable?: boolean;
  placeholder?: string;
  disabled?: boolean;
  showSelected?: boolean;
  buttonControlProps?: ButtonProps;
  className?: string;
  optionsAsync?: {
    key: string;
    fn: (search?: string) => Promise<T>;
    asyncSearch?: boolean;
    transform?: (response?: T) => Option[] | undefined;
  };

  label?: React.ReactNode;
  required?: boolean;
  onClear?: () => void;
  noPadding?: boolean;
  portalTarget?: HTMLElement | null;
  loading?: boolean;
  noScroll?: boolean;
  description?: string;
  containerStyles?: CSSObjectWithLabel;
  buttonSize?: number;
  hideClear?: boolean;
  width?: number | string;
  maxMenuHeight?: number;
  creatable?: boolean;
  noOptionsMessage?: React.ReactNode;
  isMulti?: TMulti;
  onChange: boolean extends TMulti
    ? (option: Option | Option[]) => void
    : TMulti extends true
    ? (option: Option[]) => void
    : (option: Option) => void;
  value?: boolean extends TMulti
    ? Option | Option[] | string | string[] | null
    : TMulti extends true
    ? Option[] | string[] | null
    : Option | string | null;
  // defaultValue?: boolean extends TMulti ? Option | Option[] : TMulti extends true ? Option[] : Option;
  invalid?: boolean;
  autoFocus?: boolean;
};

const Loading = styled.div`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: 2px solid var(--blue300);
  border-top-color: transparent;
  animation: select_spinner 800ms linear infinite;
  margin: 0 5px;

  @keyframes select_spinner {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const Extra = styled.span<{ $active?: boolean }>`
  color: var(--dark700);
  margin-left: 10px;
  transition: all 100ms;

  ${(props) =>
    props.$active &&
    css`
      color: var(--gray300);
    `}
`;

const Wrapper = styled.div<{ $pad: boolean }>`
  ${(props) =>
    props.$pad &&
    css`
      margin-bottom: 20px;
    `}
`;

const DropdownIndicator = (buttonSize?: number) => (props: any) => {
  return (
    <components.DropdownIndicator {...props}>
      <Button
        size={buttonSize}
        icon={props.selectProps.menuIsOpen ? ChevronUp : ChevronDown}
        iconSize={21}
        tabIndex={-1}
      />
    </components.DropdownIndicator>
  );
};

const ClearIndicator = (buttonSize?: number) => (props: any) => {
  return (
    <components.ClearIndicator {...props}>
      <Button
        size={buttonSize}
        icon={Delete}
        tooltip="Reset"
        type="danger"
        borderless
        tabIndex={-1}
      />
    </components.ClearIndicator>
  );
};

const MultiValueRemove = (props: any) => {
  return (
    <components.MultiValueRemove {...props}>
      <Button
        size={21}
        iconSize={14}
        icon={X}
        type="danger"
        borderless
        tabIndex={-1}
      />
    </components.MultiValueRemove>
  );
};

const OptionComponent = (props: any) => {
  const extra = props.options.find(
    (option: Option) => option.value === props.value
  )?.extra;
  return (
    <components.Option {...props}>
      {props.label}
      {extra && <Extra $active={props.isFocused}>{extra}</Extra>}
    </components.Option>
  );
};

const createControl = (showControl: boolean) => (props: any) => {
  return showControl ? (
    <components.Control {...props}></components.Control>
  ) : (
    <></>
  );
};

const LoadingIndicator = (_props: any) => {
  return <Loading />;
};

const SingleValue = (props: any) => {
  const extra = props.options.find(
    (option: Option) => option.value === props.data?.value
  )?.extra;
  return (
    <components.SingleValue {...props}>
      {props.children}
      {extra && <Extra>{extra}</Extra>}
    </components.SingleValue>
  );
};

const Select = <TMulti extends boolean = false, T = Option[]>(
  props: Props<TMulti, T>
) => {
  const {
    options,
    onChange,
    isSearchable,
    placeholder,
    isMulti,
    disabled,
    showSelected,
    buttonControlProps,
    optionsAsync,
    label,
    required,
    onClear,
    className,
    noPadding,
    portalTarget,
    loading,
    noScroll,
    description,
    containerStyles,
    buttonSize,
    hideClear,
    width,
    maxMenuHeight,
    creatable,
    noOptionsMessage,
    invalid,
    autoFocus,
  } = props;
  const [isOpen, setIsOpen] = useState(false);
  const [isFocussed, setIsFocussed] = useState(false);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search);

  const value = props.value as
    | Option
    | Option[]
    | string
    | string[]
    | null
    | undefined;
  const valueIsString = typeof value === "string";
  const valueIsArray = !valueIsString && !!value && "length" in value;
  const valueIsStringArray = valueIsArray && typeof value[0] === "string";
  const hasValue = !(
    !value ||
    (valueIsArray && value.length === 0) ||
    (!valueIsString && "value" in value && !value.value)
  );

  const { data, isLoading: queryIsLoading } = useQuery({
    queryKey: [
      "select_options",
      optionsAsync?.key ?? "",
      optionsAsync?.asyncSearch ? debouncedSearch : "",
    ],
    queryFn: async () => {
      if (optionsAsync) {
        const remoteOptions = await optionsAsync.fn(
          optionsAsync.asyncSearch ? debouncedSearch : undefined
        );
        return remoteOptions;
      }
    },
    enabled:
      !!optionsAsync &&
      (isOpen ||
        isFocussed ||
        (hasValue && (valueIsString || valueIsStringArray))),
  });
  const resolvedOptions = optionsAsync
    ? optionsAsync.transform?.(data) ?? (data as Option[] | undefined)
    : options;
  const isLoading = queryIsLoading || loading;

  const groupedOptions: (Option | Group)[] = [];
  resolvedOptions?.forEach((option) => {
    if (option.group) {
      const existing = groupedOptions.find(
        (group) => "options" in group && group.label === option.group
      ) as Group | undefined;
      if (existing) {
        existing.options.push(option);
      } else {
        groupedOptions.push({
          label: option.group,
          options: [option],
        });
      }
    } else {
      groupedOptions.push(option);
    }
  });

  const resolvedValue = valueIsString
    ? resolvedOptions?.find((option) => option.value === value) ?? {
        value,
        label: isLoading ? "Loading..." : "Unknown",
      }
    : valueIsStringArray
    ? value.map(
        (v): Option =>
          resolvedOptions?.find((option) => option.value === v) ?? {
            value: v as string,
            label: isLoading ? "Loading..." : "Unknown",
          }
      )
    : (value as Option | Option[] | undefined);

  const isButtonControl = !!buttonControlProps;

  const customStyles = useMemo<StylesConfig<Option, boolean>>(
    () => ({
      control: (provided, state) => ({
        ...provided,
        fontSize: 16,
        backgroundColor: state.isFocused
          ? "var(--dark400)"
          : "var(--inputBackground)",
        boxShadow: "none",
        border: `1px solid ${
          invalid
            ? "var(--red400)"
            : state.isFocused
            ? " var(--blue060)"
            : "var(--inputBorder)"
        }`,
        ":hover": {
          borderColor: invalid
            ? "var(--red400)"
            : state.isFocused
            ? "var(--blue060)"
            : "var(--inputBorder)",
        },
        borderRadius: 7,
        minHeight: 39,
        opacity: state.isDisabled ? "0.5" : undefined,
        ...containerStyles,
      }),
      valueContainer: (provided, _state) => ({
        ...provided,
        paddingLeft: 8,
        paddingRight: 0,
        gap: 5,
      }),
      input: (provided, _state) => ({
        ...provided,
        color: "var(--gray400)",
        marginTop: 0,
        marginBottom: 0,
        input: {
          height: "auto",
        },
        // gridTemplateColumns: '0 100%',
      }),
      placeholder: (provided, _state) => ({
        ...provided,
        color: "var(--dark700)",
      }),
      noOptionsMessage: (provided, _state) => ({
        ...provided,
        color: "var(--dark700)",
      }),
      multiValue: (provided, _state) => ({
        ...provided,
        backgroundColor: "var(--dark600)",
        margin: 0,
        alignItems: "center",
        borderRadius: 5,
      }),
      multiValueLabel: (provided, _state) => ({
        ...provided,
        color: "var(--gray400)",
        padding: "1.5px 5px",
        paddingRight: 2,
        fontSize: 15,
        whiteSpace: "wrap",
        wordWrap: "break-word",
        overflow: "visible",
      }),
      singleValue: (provided, _state) => ({
        ...provided,
        color: "var(--gray400)",
      }),
      loadingMessage: (provided, _state) => ({
        ...provided,
        color: "var(--dark700)",
      }),
      multiValueRemove: (_provided, _state) => ({
        paddingRight: 2,
      }),
      indicatorSeparator: () => ({
        display: "none",
      }),
      dropdownIndicator: () => ({
        marginRight: 5,
      }),
      clearIndicator: () => ({
        marginLeft: 5,
      }),
      menu: (provided, _state) => ({
        ...provided,
        margin: "3px 0",
        borderRadius: 7,
        backgroundColor: "var(--dark400)",
        boxShadow: "0 4px 5px #00000050",
        lineHeight: "normal",
        zIndex: 50004,
        border: "1px solid var(--dark500)",
        ...(isButtonControl
          ? {
              width: 150,
              right: 0,
            }
          : {}),
      }),
      menuPortal: (base) => ({ ...base, zIndex: 50005 }),
      menuList: (provided, _state) => ({
        ...provided,
        padding: "1px 0",
      }),
      option: (provided, state) => ({
        ...provided,
        borderRadius: 5,
        color: "var(--gray400)",
        backgroundColor: state.isFocused
          ? "var(--blue060)"
          : state.isSelected
          ? "var(--dark600)"
          : undefined,
        cursor: "pointer",
        transition: "all 100ms",
        fontSize: 15,
        padding: "6px 7px",
        margin: "3px 4px",
        width: "auto",
        ":active": {
          backgroundColor: "var(--blue060)",
          color: "var(--gray500)",
        },
      }),
      groupHeading: (provided, _state) => ({
        ...provided,
        color: "var(--dark700)",
        padding: "0 10px",
      }),
    }),
    [isButtonControl, containerStyles, invalid]
  );

  const showControl = !buttonControlProps;
  const Control = useMemo(() => createControl(showControl), [showControl]);

  const selectComponents = useMemo(
    () => ({
      DropdownIndicator: DropdownIndicator(buttonSize),
      MultiValueRemove,
      ClearIndicator: ClearIndicator(buttonSize),
      Control,
      LoadingIndicator,
      Option: OptionComponent,
      SingleValue,
    }),
    [buttonSize, Control]
  );

  const SelectComponent = creatable ? CreatableSelect : ReactSelect;

  return (
    <Wrapper
      $pad={!!label && !noPadding}
      className={className}
      style={{ width }}
    >
      {label && <Label label={label} required={required} hasValue={hasValue} />}
      <ClickOutside
        onClickOutside={() => buttonControlProps && setIsOpen(false)}
      >
        {buttonControlProps && (
          <Button
            {...buttonControlProps}
            selected={isOpen}
            onClick={() => {
              setIsOpen(!isOpen);
            }}
          />
        )}
        <SelectComponent
          classNamePrefix="react-select"
          options={groupedOptions}
          onChange={(option) => {
            if (option) {
              onChange(option as any);
            } else {
              onClear?.();
            }
          }}
          value={resolvedValue}
          components={selectComponents}
          isSearchable={isSearchable}
          placeholder={placeholder}
          isMulti={isMulti}
          // closeMenuOnSelect={!isMulti}
          styles={customStyles}
          // defaultValue={defaultValue}
          isDisabled={disabled}
          controlShouldRenderValue={showSelected}
          menuIsOpen={isOpen}
          menuPortalTarget={portalTarget}
          menuPosition={portalTarget ? "fixed" : "absolute"}
          onMenuOpen={() => setIsOpen(true)}
          onMenuClose={() => setIsOpen(false)}
          onFocus={() => setIsFocussed(true)}
          onBlur={() => setIsFocussed(false)}
          isLoading={isLoading}
          isClearable={(!!onClear || isMulti) && !hideClear}
          inputValue={search}
          onInputChange={setSearch}
          maxMenuHeight={maxMenuHeight}
          filterOption={({ data }, inputValue) => {
            const input = inputValue.toLowerCase();
            const { label, value, extra, group } = data;
            return (
              ((typeof label === "string" &&
                label.toLowerCase().includes(input)) ||
                value?.toLowerCase().includes(input) ||
                extra?.toLowerCase().includes(input) ||
                group?.toLowerCase().includes(input)) ??
              !inputValue
            );
          }}
          menuShouldScrollIntoView={!noScroll}
          noOptionsMessage={() => noOptionsMessage}
          autoFocus={autoFocus}
        />
      </ClickOutside>
      {description && <Description label={description} />}
    </Wrapper>
  );
};

export default Select;
